const fetcher = (url: string, options?: RequestInit) =>
  fetch(url, { credentials: 'include', ...options }).then(async (res) => {
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  });

export interface ContactList {
  id: number;
  workspaceId: number;
  campaignId: string | null;
  campaignName: string | null;
  name: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Contact {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  company: string | null;
  companyWebsite: string | null;
  linkedinUrl: string | null;
  location: string | null;
}

export interface ContactListDetail extends ContactList {
  contacts: Contact[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const contactListService = {
  getLists: (): Promise<ContactList[]> =>
    fetcher('/api/contact-lists'),

  getList: (id: number, params?: { page?: number; limit?: number; search?: string }): Promise<ContactListDetail> => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    const query = qs.toString();
    return fetcher(`/api/contact-lists/${id}${query ? `?${query}` : ''}`);
  },

  createList: (data: { name: string; campaignId?: string }): Promise<ContactList> =>
    fetcher('/api/contact-lists', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  updateList: (id: number, data: { name: string }): Promise<ContactList> =>
    fetcher(`/api/contact-lists/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  deleteList: (id: number): Promise<void> =>
    fetcher(`/api/contact-lists/${id}`, { method: 'DELETE' }),

  addContacts: (listId: number, contactIds: number[]): Promise<{ success: boolean; added: number }> =>
    fetcher(`/api/contact-lists/${listId}/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contactIds }),
    }),

  removeContacts: (listId: number, contactIds: number[]): Promise<void> =>
    fetcher(`/api/contact-lists/${listId}/contacts`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contactIds }),
    }),

  autoProspect: (listId: number, count = 25): Promise<{
    added: number;
    fetched: number;
    sessionId: string;
    cursor: { nextPage: number; exhausted?: boolean } | null;
    exhausted: boolean;
    filterSource: 'campaign' | 'workspace' | 'default';
  }> =>
    fetcher(`/api/contact-lists/${listId}/auto-prospect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ count }),
    }),
};
